import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import API_CONFIG from '../config/api';

interface Guide {
  _id: string;
  title: string;
  content: string;
  tab: 'general' | 'test-request' | 'test-results' | 'remaining-payment';
  active: boolean;
  order: number;
}

const Help: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch guides from API
  const fetchGuides = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.getUrl(`guides/public/tab/${activeTab}`));
      
      if (!response.ok) {
        throw new Error('خطا در دریافت راهنماها');
      }
      
      const data = await response.json();
      setGuides(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت راهنماها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuides();
  }, [activeTab]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <DashboardLayout>
      <div className="p-4">
        <div className="card bg-white rounded-sm p-4 border border-slate-300">
          <h2 className="text-lg font-bold text-slate-800 mb-4">راهنما</h2>
          
          <div className="border-b border-slate-200 mb-4">
            <nav className="flex items-center gap-2 -m-px" role="tablist" aria-label="تب‌های راهنما">
              <button
                onClick={() => handleTabChange('general')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'general'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                راهنمای کلی
              </button>
              <button
                onClick={() => handleTabChange('test-request')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'test-request'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                پک راهنمای ثبت درخواست آزمایش
              </button>
              <button
                onClick={() => handleTabChange('test-results')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'test-results'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                پک راهنمای دریافت تصویر جواب آزمایش
              </button>
              <button
                onClick={() => handleTabChange('remaining-payment')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'remaining-payment'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                پرداخت باقی مانده
              </button>
            </nav>
          </div>

          {activeTab === 'general' && (
            <div className="tab-content">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-lg">در حال بارگذاری...</div>
                </div>
              ) : error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              ) : guides.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  راهنمایی برای این بخش موجود نیست.
                </div>
              ) : (
                guides.map((guide) => (
                  <div key={guide._id} className="mb-6">
                    <h3 className="text-md font-semibold text-slate-800 mb-3">{guide.title}</h3>
                    <div 
                      className="text-slate-600 mb-4 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: guide.content.replace(/\n/g, '<br />') }}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'test-request' && (
            <div className="tab-content">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-lg">در حال بارگذاری...</div>
                </div>
              ) : error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              ) : guides.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  راهنمایی برای این بخش موجود نیست.
                </div>
              ) : (
                guides.map((guide) => (
                  <div key={guide._id} className="mb-6">
                    <h3 className="text-md font-semibold text-slate-800 mb-3">{guide.title}</h3>
                    <div 
                      className="text-slate-600 mb-4 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: guide.content.replace(/\n/g, '<br />') }}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'test-results' && (
            <div className="tab-content">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-lg">در حال بارگذاری...</div>
                </div>
              ) : error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              ) : guides.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  راهنمایی برای این بخش موجود نیست.
                </div>
              ) : (
                guides.map((guide) => (
                  <div key={guide._id} className="mb-6">
                    <h3 className="text-md font-semibold text-slate-800 mb-3">{guide.title}</h3>
                    <div 
                      className="text-slate-600 mb-4 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: guide.content.replace(/\n/g, '<br />') }}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'remaining-payment' && (
            <div className="tab-content">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-lg">در حال بارگذاری...</div>
                </div>
              ) : error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              ) : guides.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  راهنمایی برای این بخش موجود نیست.
                </div>
              ) : (
                guides.map((guide) => (
                  <div key={guide._id} className="mb-6">
                    <h3 className="text-md font-semibold text-slate-800 mb-3">{guide.title}</h3>
                    <div 
                      className="text-slate-600 mb-4 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: guide.content.replace(/\n/g, '<br />') }}
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
      </div>
    </DashboardLayout>
  );
};

export default Help;
